import { type FieldValues, type Path, type UseFormSetError } from 'react-hook-form'
import { translateUserFacingText } from './userFacingTerminology'

/**
 * Turns a 422 (or any field-keyed error body) into a modal banner and
 * react-hook-form field errors so the matching input is highlighted.
 * Used by Create and Edit modals only.
 */

const ROOT_MESSAGE_KEYS = new Set(['error', 'message', 'detail', 'description', 'errorMessage'])
const MAX_DEPTH = 6
const ISO_VALUE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2})/

export type ServerFieldErrorSetter = (
  name: string,
  error: { type: string, message?: string },
  options?: { shouldFocus?: boolean }
) => void

export function bindSetError<T extends FieldValues> (
  setError: UseFormSetError<T>
): ServerFieldErrorSetter {
  return (name, error, options) => {
    setError(name as Path<T>, error, { shouldFocus: options?.shouldFocus ?? false })
  }
}

interface FieldFailure {
  path: string
  message: string
}

const humanizeSegment = (key: string): string => {
  if (/^\d+$/.test(key)) return `Item ${Number(key) + 1}`
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .toLowerCase()
  if (!words) return key
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const humanizePath = (path: string): string =>
  path.split('.').filter(Boolean).map(humanizeSegment).join(' — ')

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const pushMessage = (path: string, message: string, out: FieldFailure[]): void => {
  const trimmed = message.trim()
  if (!trimmed || ISO_VALUE.test(trimmed)) return
  out.push({ path, message: trimmed })
}

const walk = (value: unknown, path: string, out: FieldFailure[], depth: number): void => {
  if (value == null || depth > MAX_DEPTH) return

  if (typeof value === 'string') {
    pushMessage(path, value, out)
    return
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (typeof item === 'string') {
        pushMessage(path, item, out)
        return
      }
      const next = path ? `${path}.${index}` : String(index)
      walk(item, next, out, depth + 1)
    })
    return
  }

  if (!isRecord(value)) return

  Object.entries(value).forEach(([key, child]) => {
    const isRootMessage = path === '' && ROOT_MESSAGE_KEYS.has(key)
    const next = isRootMessage ? '' : (path ? `${path}.${key}` : key)
    walk(child, next, out, depth + 1)
  })
}

const readErrorParts = (error: unknown): { data: unknown, message: string } => {
  if (!isRecord(error)) {
    return { data: undefined, message: typeof error === 'string' ? error : '' }
  }
  const response = isRecord(error.response) ? error.response : undefined
  const data = response && 'data' in response ? response.data : error.data
  const message = typeof error.message === 'string' ? error.message : ''
  return { data, message }
}

/**
 * Banner lines for ValidationAlert, and field errors via setError when a path is present.
 * Root keys error/message/detail/description/errorMessage stay banner-only.
 * ISO timestamps are skipped so boundary metadata is not treated as a field error.
 */
export const showServerFieldErrors = (
  error: unknown,
  setError?: ServerFieldErrorSetter,
  clearErrors?: () => void
): string[] => {
  clearErrors?.()

  const { data, message } = readErrorParts(error)
  const failures: FieldFailure[] = []
  walk(data, '', failures, 0)

  const banner = failures.map(({ path, message: fieldMessage }) => {
    const text = translateUserFacingText(fieldMessage)
    return path ? `${humanizePath(path)}: ${text}` : text
  })

  if (setError) {
    const byPath = new Map<string, string[]>()
    failures.forEach(({ path, message: fieldMessage }) => {
      if (!path) return
      const text = translateUserFacingText(fieldMessage)
      const existing = byPath.get(path) ?? []
      existing.push(text)
      byPath.set(path, existing)
    })

    let focused = false
    byPath.forEach((messages, path) => {
      setError(path, { type: 'server', message: messages.join(' ') }, { shouldFocus: !focused })
      focused = true
    })
  }

  if (banner.length > 0) return banner
  if (message.trim()) return [translateUserFacingText(message.trim())]
  return ['Unknown error']
}
